import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ShoppingBag, Instagram } from "lucide-react";

const INSTAGRAM_URL = "https://www.instagram.com/vison_studios/";

const NAV_ITEMS = [
  { label: "Virtual Store", to: "/" },
  { label: "Home", to: "/home" },
  { label: "Members Home", to: "/members" },
  { label: "Shop", to: "/shop" },
  { label: "T-shirts", to: "/t-shirts" },
  { label: "Long Sleeves", to: "/long-sleeves" },
  { label: "Hoodies", to: "/hoodies" },
  { label: "Tops", to: "/tops" },
  { label: "Bottoms", to: "/bottoms" },
  { label: "Accessories", to: "/accessories" },
  { label: "Policies", to: "/policies" },
  { label: "Gallery", to: "/gallery" },
  { label: "Contact", to: "/contact" },
  { label: "Pre-order", to: "/preorder" },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed inset-x-0 top-0 z-50 border-b border-neutral-200 bg-white/95 backdrop-blur-sm"
      >
        <div className="relative mx-auto flex h-14 max-w-5xl items-center justify-between px-4">
          <button
            type="button"
            aria-label="Open menu"
            data-testid="menu-button"
            onClick={() => setOpen(true)}
            className="flex h-10 w-10 items-center justify-center text-neutral-900 transition-colors hover:text-neutral-500"
          >
            <Menu className="h-6 w-6" strokeWidth={1.75} />
          </button>

          <Link
            to="/home"
            aria-label="Vison Studios home"
            data-testid="header-logo"
            className="absolute left-1/2 -translate-x-1/2"
          >
            <img src="/images/vison-logo.webp" alt="VISON" className="h-5 w-auto" draggable="false" />
          </Link>

          <div className="flex items-center">
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Vison Studios on Instagram"
              data-testid="header-instagram-link"
              className="flex h-10 w-10 items-center justify-center text-neutral-900 transition-colors hover:text-neutral-500"
            >
              <Instagram className="h-5 w-5" strokeWidth={1.75} />
            </a>
            <Link
              to="/preorder"
              aria-label="Pre-order"
              data-testid="header-bag"
              className="flex h-10 w-10 items-center justify-center text-neutral-900 transition-colors hover:text-neutral-500"
            >
              <ShoppingBag className="h-5 w-5" strokeWidth={1.75} />
            </Link>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              data-testid="menu-overlay"
              className="fixed inset-0 z-[60] bg-black/30"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <motion.aside
              data-testid="menu-panel"
              className="fixed inset-y-0 left-0 z-[70] flex w-72 max-w-[85vw] flex-col bg-white shadow-2xl"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 320, damping: 32 }}
            >
              <div className="flex h-14 items-center border-b border-neutral-200 px-4">
                <button
                  type="button"
                  aria-label="Close menu"
                  data-testid="close-menu-button"
                  onClick={() => setOpen(false)}
                  className="flex h-10 w-10 items-center justify-center text-neutral-900 transition-colors hover:text-neutral-500"
                >
                  <X className="h-5 w-5" strokeWidth={1.75} />
                </button>
              </div>
              <nav className="flex-1 overflow-y-auto px-4 py-4">
                {NAV_ITEMS.map((item) => {
                  const active = location.pathname === item.to;
                  return (
                    <button
                      key={item.to}
                      type="button"
                      data-testid={`nav-link-${slug(item.label)}`}
                      onClick={() => go(item.to)}
                      className={`block w-full rounded-md px-3 py-2.5 text-left text-sm transition-colors ${
                        active
                          ? "bg-neutral-100 font-semibold text-neutral-900"
                          : "text-neutral-500 hover:bg-neutral-50 hover:text-neutral-900"
                      }`}
                    >
                      {item.label}
                    </button>
                  );
                })}
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
